import { Component, OnInit } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { UserService } from "src/app/authentication/user.service";
import { PaymentsService } from "src/app/services/payments.service";
import { ExternalLinkService } from "@app/services/external-link.service";
import { MobileService } from "src/app/services/mobile.service";
import { environment } from "src/environments/environment";
import {
  AccountService,
  LoginRequest,
  LoginResponse,
  Status,
} from "src/swagger/api.client.generated";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private accountClient: AccountService,
    private userService: UserService,
    private paymentsService: PaymentsService,
    private externalLink: ExternalLinkService,
    private router: Router,
    public mobileService: MobileService
  ) {}

  loginForm: UntypedFormGroup;
  error: string;
  returnUrl: string;
  storedUserName: string;
  registeredSuccess: boolean;
  showPasswordText = false;
  readonly supportMailto =
    "mailto:info@lglabs.co?subject=" + encodeURIComponent("Trouble signing in to flip*");
  // The mobile app always offers sign-up; the browser only does off production.
  readonly showWebRegisterLink: boolean = environment.showWebRegisterLink;

  ngOnInit() {
    this.storedUserName = this.userService.getUserName();

    this.loginForm = this.formBuilder.group({
      username: [this.storedUserName, Validators.required],
      password: ["", Validators.required],
    });

    this.returnUrl = this.route.snapshot.queryParams["returnUrl"] || "/";
    this.registeredSuccess = this.route.snapshot.queryParams["registered"] === "true";

    if (this.userService.isUserLoggedIn()) {
      this.routeAfterLogin();
    }
  }

  get f() {
    return this.loginForm.controls;
  }

  openExternalLink(event: Event, url: string): void {
    event.preventDefault();
    this.externalLink.open(url);
  }

  // In a browser the anchor's own mailto: href opens the mail client. Inside the
  // mobile WebView that does nothing, so hand it to the native side instead.
  openSupportEmail(event: Event): void {
    if (this.externalLink.isInMobileApp()) {
      event.preventDefault();
      this.externalLink.open(this.supportMailto);
    }
  }

  togglePassword(): void {
    this.showPasswordText = !this.showPasswordText;
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      return;
    }

    this.accountClient
      .login(
        new LoginRequest({
          username: this.f.username.value.toLowerCase(),
          password: this.f.password.value,
        })
      )
      .subscribe((result) => {
        switch (result.resultStatus.statusCode) {
          case Status.Success:
            this.authorizeUser(result.responseData);
            break;
          default:
            this.error = result.resultStatus.errorText;
            break;
        }
      });
  }

  private async authorizeUser(model: LoginResponse) {
    this.userService.saveUser(model);

    // Accounts are only ever created via the register -> subscribe -> pay flow, so a
    // logged-in account with no active subscription is an edge case (e.g. a lapsed/
    // canceled subscription, or one that never finished checkout). The user did just
    // authenticate correctly though, so keep them logged in and send them to finish
    // subscribing rather than logging them back out.
    let subscriptionActive: boolean;
    try {
      subscriptionActive = await this.paymentsService.isSubscriptionActive();
    } catch {
      // Fail closed, matching SubscriptionGuard's behavior.
      subscriptionActive = false;
    }

    if (!subscriptionActive) {
      this.router.navigate(["/stripe-payments"], { queryParams: { resume: "1" } });
      return;
    }

    this.routeAfterLogin();
  }

  /**
   * Routes the user to their intended destination after a successful login.
   */
  routeAfterLogin() {
    if (!this.userService.isOnboardingComplete()) {
      this.router.navigate(["/onboarding"]);
      return;
    }
    this.router.navigate([this.returnUrl]);
  }
}
